import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    GetAllUsers,
    GetUserById,
    AddNewUser,
    UpdateUser,
    DeleteUser,
    UpdateUserMeetingRoomQuota,
    UpdateUserStatus,
    SendResetPasswordEmail as SendResetPasswordEmailBE,
    AddTeamMemberByAdmin,
    ForceResetPassword,
    RevealPassword,
    MarkPasswordChanged,
    DeleteTeamMember,
    UpdateTeamMemberPermissions,
    UpdateTeamMemberMeetingRoomQuota,
} from '../services/backend';
import { Notify, NotifyError } from '../utils/functions';
import { SendResetPasswordEmail } from '../services/firebase/auth';
import { STATIC_USER_ROLES } from '../utils/enums';

export function useGetUsers(filters = {}) {
    return useQuery(['users', filters], () => GetAllUsers(filters), {
        refetchOnWindowFocus: true, // Auto-refetch
        staleTime: 0, // Never keep stale data
        cacheTime: 0, // Immediately clear cache
        refetchOnMount: 'always', // Always fetch fresh data on component mount
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch users", error);
            console.error("Error fetching users:", error);
        }
    });
}

export function useGetUserById(userId) {
    return useQuery(
        ['user', userId],
        () => GetUserById(userId),
        {
            enabled: !!userId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            onError: (error) => {
                NotifyError("Failed to fetch user details", error);
                console.error("Error fetching user details:", error);
            }
        }
    );
}

export function useAddUser() {
    const queryClient = useQueryClient();
    return useMutation(AddNewUser, {
        onSuccess: () => {
            Notify("User added successfully", "success");
            queryClient.invalidateQueries('users');
            queryClient.invalidateQueries('customersStats');
        },
        onError: (error) => {
            // Don't show toast here — callers (leadFormModal, customerFormModal) handle their own error toasts
            console.error("Error adding user:", error);
        }
    });
}

export function useUpdateUser() {
    const queryClient = useQueryClient();
    return useMutation(({ userId, data }) => UpdateUser(userId, data), {
        onSuccess: (data, variables) => {
            Notify("User updated successfully", "success");
            queryClient.invalidateQueries('users');
            queryClient.invalidateQueries(['user', variables.userId]);
        },
        onError: (error) => {
            // Don't show toast here — callers handle their own error toasts
            console.error("Error updating user:", error);
        }
    });
}

export function useDeleteUser() {
    const queryClient = useQueryClient();
    return useMutation(DeleteUser, {
        onSuccess: () => {
            Notify("User deleted successfully", "success");
            queryClient.invalidateQueries('users');
            queryClient.invalidateQueries('teamMembers');
        },
        onError: (error) => {
            NotifyError("Failed to delete user", error);
            console.error("Error deleting user:", error);
        }
    });
}

export function useDeleteTeamMember() {
    const queryClient = useQueryClient();
    return useMutation(DeleteTeamMember, {
        onSuccess: () => {
            Notify("Team member deleted successfully", "success");
            queryClient.invalidateQueries('users');
            queryClient.invalidateQueries('teamMembers');
        },
        onError: (error) => {
            console.error("Error deleting team member:", error);
            NotifyError("Failed to delete team member", error);
        }
    });
}

export function useUpdateUserMeetingRoomQuota() {
    const queryClient = useQueryClient();
    return useMutation(({ userId, data }) => UpdateUserMeetingRoomQuota(userId, data), {
        onSuccess: async (data, variables) => {
            Notify("User meeting room quota updated successfully", "success");
            await Promise.all([
                queryClient.invalidateQueries('users'),
                queryClient.invalidateQueries(['user', variables.userId])
            ]);
        },
        onError: (error) => {
            NotifyError("Failed to update user meeting room quota", error);
            console.error("Error updating user meeting room quota:", error);
        }
    });
}

export function useUpdateTeamMemberMeetingRoomQuota() {
    const queryClient = useQueryClient();
    return useMutation(({ userId, data }) => UpdateTeamMemberMeetingRoomQuota(userId, data), {
        onSuccess: async (data, variables) => {
            Notify("Team member meeting room quota updated successfully", "success");
            await Promise.all([
                queryClient.invalidateQueries('users'),
                queryClient.invalidateQueries(['user', variables.userId])
            ]);
        },
        onError: (error) => {
            NotifyError("Failed to update team member meeting room quota", error);
            console.error("Error updating team member meeting room quota:", error);
        }
    });
}

export function useUpdateTeamMemberPermissions() {
    const queryClient = useQueryClient();
    return useMutation(({ userId, data }) => UpdateTeamMemberPermissions(userId, data), {
        onSuccess: (data, variables) => {
            Notify("Team member permissions updated successfully", "success");
            queryClient.invalidateQueries('users');
            queryClient.invalidateQueries(['user', variables.userId]);
        },
        onError: (error) => {
            NotifyError("Failed to update team member permissions", error);
            console.error("Error updating team member permissions:", error);
        }
    });
}

export function useUpdateUserStatus() {
    const queryClient = useQueryClient();
    return useMutation(({ userId, status }) => UpdateUserStatus(userId, status), {
        onSuccess: (data, variables) => {
            Notify("User status updated successfully", "success");
            queryClient.invalidateQueries('users');
            queryClient.invalidateQueries(['user', variables.userId]);
        },
        onError: (error) => {
            NotifyError("Failed to update user status", error);
            console.error("Error updating user status:", error);
        }
    });
}

export function useSendResetPasswordEmail() {
    const queryClient = useQueryClient();
    return useMutation(async ({ email, userId }) => {
        await SendResetPasswordEmail(email)
        await SendResetPasswordEmailBE(userId);
    }, {
        onSuccess: () => {
            Notify("Reset password email sent successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError("Failed to send reset password email", error);
            console.error("Error sending reset password email:", error);
        }
    });
}

export function useForceResetPassword() {
    const queryClient = useQueryClient();
    return useMutation(async ({ userId, password }) => {
        return await ForceResetPassword(userId, password);
    }, {
        onSuccess: (data) => {
            Notify("Force reset password successfully", "success");
            queryClient.invalidateQueries('users');
            queryClient.invalidateQueries(['user']);
        },
        onError: (error) => {
            NotifyError("Failed to force reset password", error);
            console.error("Error force resetting password:", error);
        }
    });
}

export function useRevealPassword() {
    return useMutation(async ({ userId }) => {
        return await RevealPassword(userId);
    }, {
        // Only log onError, handle success manually
        onError: (error) => {
            NotifyError("Failed to reveal password", error);
            console.error("Error revealing password:", error);
        }
    });
}

export function useMarkPasswordChanged() {
    return useMutation(async () => {
        return await MarkPasswordChanged();
    }, {
        onError: (error) => {
            console.error("Error marking password changed:", error);
        }
    });
}

export function useGetTeamMembers(customerId) {
    return useQuery(['team-members', customerId], () => GetAllUsers({
        role: STATIC_USER_ROLES.TEAM_MEMBER,
        teamId: customerId
    }), {
        enabled: !!customerId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch team members", error);
            console.error("Error fetching team members:", error);
        }
    });
}

export function useAddTeamMember() {
    const queryClient = useQueryClient();
    return useMutation(AddTeamMemberByAdmin, {
        onSuccess: (data) => {
            Notify(data?.data?.message || "User team member updated successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            // Don't show toast here — callers handle their own error toasts
            console.error("Error adding team member:", error);
        }
    });
}
